import { useState } from "react";
import type { ReactNode } from "react";

import { useDispatch } from "react-redux";

import type { CaseSchema } from "@/api/generated/types.gen";
import { UpdateCaseSchema } from "@/components/CasesList/schema/case";
import FileDisplayCard from "@/components/FileDisplayCard";
import { FilePreview } from "@/components/FilePreview";
import type { FilePreviewProps } from "@/components/FilePreview";
import { useAppForm } from "@/components/Form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ModalCanvas, ModalSection } from "@/components/ui/modal-section";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Switch } from "@/components/ui/switch";
import { SERVICE_TYPES } from "@/constants/service-types";
import {
  useAssignCaseDoctor,
  useSetDoctorReady,
} from "@/hooks/mutations/use-admin-cases";
import { useUpdateCase } from "@/hooks/mutations/use-cases";
import { useDoctorOptions } from "@/hooks/queries/use-doctor-options";
import { useIsAdmin, useIsDoctorView } from "@/hooks/queries/use-me";
import { useTemplates } from "@/hooks/queries/use-templates";
import { setMessage } from "@/redux/notification";

import { ReactComponent as ViewEditIcon } from "@assets/svg/eye-with-pencil.svg";

import { DoctorAccessBadge } from "@components/DoctorAccessBadge";
import { ReleaseConfirmDialog } from "@components/ReleaseConfirmDialog";

import {
  DOCTOR_ACCESS_COPY,
  doctorAccessState,
  readyNoteFor,
} from "@constants/doctor-access";

import { fetchCaseTemplatePreviewUrl } from "@hooks/queries/use-case-template";
import { useObjectUrl } from "@hooks/useObjectUrl";

export type UpdateCaseModalProps = {
  caseItem: CaseSchema;
  trigger?: ReactNode;
};

// Matches the Filter popup's label styling (FilterBar.tsx) so form labels
// read at a consistent size across the app instead of the heavier default
// from FieldWrapper.
const FIELD_LABEL_CLASS = "text-xs";

export const UpdateCaseModal = ({ caseItem, trigger }: UpdateCaseModalProps) => {
  const dispatch = useDispatch();
  const updateCase = useUpdateCase();
  // MDRS-230: doctor-ready is admin-only and lives on a separate admin endpoint,
  // so it's tracked outside the form and PATCHed on its own (see handleSubmit).
  const isAdmin = useIsAdmin();
  // MDRS-325: the assigned doctor loses the Assessment section entirely — the
  // Case page hides those three fields, and leaving them editable one button
  // away on that same page would defeat it. Enforced server-side too
  // (core/authz.may_edit_service_fields), which is why the payload drops them
  // rather than just the rendering.
  const isDoctorView = useIsDoctorView();
  // useUpdateCase already invalidates the cases cache on save; opting out here
  // avoids refetching every mounted case query twice per save.
  const setDoctorReady = useSetDoctorReady({ invalidate: false });

  const assignDoctor = useAssignCaseDoctor();

  const [isOpen, setIsOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isDoctorReady, setIsDoctorReady] = useState(
    Boolean(caseItem.is_doctor_ready)
  );
  const [doctorId, setDoctorId] = useState<string>(
    (caseItem.user_id as unknown as string) ?? ""
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { options: doctors, isLoading: isUsersLoading } = useDoctorOptions();

  const accessState = doctorAccessState(Boolean(doctorId), isDoctorReady);
  // Named in the toggle hint ("Dr Elena Marsh cannot open this case yet"), which
  // is more actionable than "the assigned doctor". Falls back to the case's own
  // doctor_name while the admin users list is still loading.
  const selectedDoctorName =
    doctors.find((d) => d.value === doctorId)?.label ?? caseItem.doctor_name;

  /**
   * Clearing the doctor forces the case back to hidden — a released case with
   * nobody assigned is a state the UI shouldn't be able to produce.
   */
  const handleDoctorSelect = (nextId: string) => {
    setDoctorId(nextId);
    if (!nextId) setIsDoctorReady(false);
  };

  const { data: templates = [], isLoading: isTemplatesLoading } = useTemplates();

  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: template.name,
  }));

  const getFileName = () => {
    if (!caseItem.report_template_path) return "No file";
    return caseItem.report_template_path.split("/").pop() || "Report Template";
  };

  const getFileType = (): FilePreviewProps["fileType"] => {
    const ext = getFileName().split(".").pop()?.toLowerCase();
    if (ext === "pdf") return "pdf";
    if (ext === "docx" || ext === "doc") return "docx";
    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext || ""))
      return "image";
    return "other";
  };

  // MDRS-503: the case payload no longer carries a pre-signed template URL —
  // the list used to mint one 8h SAS per row on every page load. The bytes are
  // fetched from the authenticated proxy when the user actually opens the
  // preview, so this is state rather than a derived value.
  const [previewUrl, setPreviewUrl] = useObjectUrl();

  const handlePreview = async () => {
    if (!caseItem.id || !caseItem.report_template_path) {
      dispatch(
        setMessage({
          content: "Preview URL not available for this file",
          type: "error",
        })
      );
      return;
    }
    try {
      setPreviewUrl(await fetchCaseTemplatePreviewUrl(caseItem.id));
      setShowPreview(true);
    } catch {
      dispatch(
        setMessage({
          content: "Preview URL not available for this file",
          type: "error",
        })
      );
    }
  };

  const form = useAppForm({
    defaultValues: {
      case_number: caseItem.case_number || "",
      claim_number: caseItem.claim_number || "",
      examinee_name: caseItem.examinee_name || "",
      template_id: caseItem.template?.id || "",
      organisation: caseItem.organisation || "",
      service_type: caseItem.service_type || "",
      case_type: caseItem.case_type || "",
      report_due_date: caseItem.report_due_date || "",
      appointment_date: caseItem.appointment_date || "",
    },
    validators: {
      onBlur: UpdateCaseSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateCase.mutateAsync({
          caseId: caseItem.id,
          updateData: {
            case_number: value.case_number || undefined,
            claim_number: value.claim_number || undefined,
            examinee_name: value.examinee_name || undefined,
            organisation: value.organisation || undefined,
            // Omitted entirely for a doctor: the form still holds the values it
            // was seeded with, and posting them back would 403 the whole save —
            // taking their permitted edits down with it. One predicate drives
            // both the rendering and the payload, so a field that isn't shown is
            // never sent. Safe to omit: PATCH is partial, so leaving a field out
            // preserves it rather than clearing it. That also covers the
            // fail-closed window where `/me` hasn't resolved — the section is
            // hidden there too, so there is no edit to drop.
            ...(isDoctorView
              ? {}
              : {
                  template_id: value.template_id || undefined,
                  service_type: value.service_type || undefined,
                  case_type: value.case_type || undefined,
                }),
            report_due_date: value.report_due_date || undefined,
            appointment_date: value.appointment_date || undefined,
          },
        });

        // MDRS-234: persist a doctor reassignment BEFORE the release flag, so a
        // release always applies to the doctor the admin actually chose. Same
        // partial-success handling as below — the fields are already saved.
        if (doctorId && doctorId !== (caseItem.user_id as unknown as string)) {
          try {
            await assignDoctor.mutateAsync({
              caseId: caseItem.id as unknown as string,
              userId: doctorId,
            });
          } catch (error) {
            setDoctorId((caseItem.user_id as unknown as string) ?? "");
            dispatch(
              setMessage({
                content: `Case details saved, but reassigning the doctor failed: ${
                  error instanceof Error ? error.message : "unknown error"
                }`,
                type: "error",
              })
            );
            return;
          }
        }

        // Doctor-ready writes to a separate admin-only endpoint, so only call it
        // when the flag actually changed — a plain edit shouldn't need admin
        // rights. The toggle only renders for admins, so this can't fire for a
        // non-admin; the backend enforces `is_admin` regardless.
        //
        // Caught separately: the case fields are already saved by this point, so
        // a failure here is a PARTIAL success. Reporting it as "Failed to update
        // case" would tell the admin nothing saved when most of it did.
        if (isDoctorReady !== Boolean(caseItem.is_doctor_ready)) {
          try {
            await setDoctorReady.mutateAsync({
              caseId: caseItem.id as unknown as string,
              isDoctorReady,
            });
          } catch (error) {
            // Snap the toggle back so it reflects what's actually persisted.
            setIsDoctorReady(Boolean(caseItem.is_doctor_ready));
            dispatch(
              setMessage({
                content: `Case details saved, but the doctor-ready change failed: ${
                  error instanceof Error ? error.message : "unknown error"
                }`,
                type: "error",
              })
            );
            return;
          }
        }

        dispatch(
          setMessage({
            content: "Case updated successfully",
            type: "success",
          })
        );

        setIsOpen(false);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update case";
        dispatch(
          setMessage({
            content: errorMessage,
            type: "error",
          })
        );
      }
    },
  });

  /** Re-seed the admin-only controls from the case (they live outside the form). */
  const resetAccessState = () => {
    setIsDoctorReady(Boolean(caseItem.is_doctor_ready));
    setDoctorId((caseItem.user_id as unknown as string) ?? "");
  };

  const handleCancel = () => {
    form.reset();
    resetAccessState();
    setIsOpen(false);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setShowPreview(false);
    // Re-seed on open so a cancelled edit doesn't reappear as the starting state.
    if (open) resetAccessState();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleModalOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="sm" className="p-2">
            <ViewEditIcon className="size-5 fill-primary" />
          </Button>
        )}
      </DialogTrigger>

      <DialogContent
        className={`flex max-h-[85vh] flex-col gap-2 overflow-hidden rounded-2xl bg-background px-6 pb-8 ${
          showPreview ? "min-w-[900px] max-w-4xl" : "min-w-[600px] max-w-lg"
        }`}
      >
        {showPreview ? (
          <>
            <div className="relative mb-4 flex w-full items-center">
              <div className="flex flex-1 justify-center">
                <DialogTitle className="text-center text-xl font-semibold">
                  {getFileName()}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  File preview
                </DialogDescription>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto" style={{ height: "70vh" }}>
              <FilePreview
                fileUrl={previewUrl}
                fileType={getFileType()}
                filename={getFileName()}
                showZoomControls={false}
              />
            </div>
          </>
        ) : (
          <>
            <DialogTitle className="text-xl font-semibold tracking-[-0.01em]">
              Update case
            </DialogTitle>
            {/* Which case is being edited — the modal opens from a row, so without
                this there's nothing on screen confirming the target. */}
            <DialogDescription className="text-[13px] text-foreground-muted">
              Case {caseItem.case_number}
              {caseItem.examinee_name ? ` · ${caseItem.examinee_name}` : ""}
            </DialogDescription>

            {caseItem.report_template_path && !isDoctorView && (
              <FileDisplayCard
                fileName={getFileName()}
                fileSize=""
                // MDRS-503: gated on the template's existence, not on
                // `previewUrl` — that now starts null and is only filled by
                // `handlePreview`, so gating on it disabled the very button
                // that fills it.
                disabled={!caseItem.report_template_path}
                onPreview={handlePreview}
              />
            )}

            <form.AppForm>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  form.handleSubmit();
                }}
                className="flex min-h-0 flex-1 flex-col space-y-4"
              >
                {/* MDRS-230: fields grouped into cards on a grey canvas rather
                    than one flat column. flex-1/min-h-0 make this the sole
                    scrollable region — the outer max-h-[85vh] flex column
                    caps the dialog, and only the canvas grows/scrolls to fill
                    what's left instead of every row sharing the leftover
                    height. */}
                <ModalCanvas className="max-h-full min-h-0 flex-1">
                  <ModalSection
                    title={DOCTOR_ACCESS_COPY.examineeCardTitle}
                    description={DOCTOR_ACCESS_COPY.examineeCardHint}
                  >
                    <form.AppField name="examinee_name">
                      {(field) => (
                        <field.InputField
                          label="Examinee name"
                          labelClassName={FIELD_LABEL_CLASS}
                          placeholder="Enter examinee name"
                          required
                        />
                      )}
                    </form.AppField>
                    <form.AppField name="claim_number">
                      {(field) => (
                        <field.InputField
                          label="Claim number"
                          labelClassName={FIELD_LABEL_CLASS}
                          placeholder="Enter claim number"
                          required
                        />
                      )}
                    </form.AppField>
                    <form.AppField name="organisation">
                      {(field) => (
                        <field.InputField
                          label="Organisation"
                          labelClassName={FIELD_LABEL_CLASS}
                          placeholder="Enter organisation"
                        />
                      )}
                    </form.AppField>
                  </ModalSection>

                  {/* MDRS-325: the whole section is internal service
                      configuration, hidden from the assigned doctor. Non-admin
                      internal staff keep it — they set these during prep. */}
                  {!isDoctorView && (
                    <ModalSection
                      title={DOCTOR_ACCESS_COPY.assessmentCardTitle}
                      description={DOCTOR_ACCESS_COPY.assessmentCardHint}
                    >
                      <form.AppField name="service_type">
                        {(field) => (
                          <field.SelectField
                            label="Service type"
                            labelClassName={FIELD_LABEL_CLASS}
                            subLabel={DOCTOR_ACCESS_COPY.serviceTypeHint}
                            placeholder="Select a service type"
                            options={SERVICE_TYPES.map((s) => ({
                              value: s.value,
                              label: s.label,
                            }))}
                          />
                        )}
                      </form.AppField>
                      {/* MDRS-310: free-text categorisation. Descriptive only —
                        nothing downstream reads it. */}
                      <form.AppField name="case_type">
                        {(field) => (
                          <field.InputField
                            label="Case type"
                            labelClassName={FIELD_LABEL_CLASS}
                            placeholder="Enter case type"
                            maxLength={100}
                          />
                        )}
                      </form.AppField>
                      <form.AppField name="template_id">
                        {(field) => (
                          <field.SelectField
                            label="Report template"
                            labelClassName={FIELD_LABEL_CLASS}
                            placeholder="Select a template"
                            options={templateOptions}
                            loading={isTemplatesLoading}
                            required
                          />
                        )}
                      </form.AppField>
                    </ModalSection>
                  )}

                  <ModalSection
                    title={DOCTOR_ACCESS_COPY.scheduleCardTitle}
                    description={DOCTOR_ACCESS_COPY.scheduleCardHint}
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <form.AppField name="appointment_date">
                        {(field) => (
                          <field.DateField
                            label="Appointment date"
                            labelClassName={FIELD_LABEL_CLASS}
                          />
                        )}
                      </form.AppField>
                      <form.AppField name="report_due_date">
                        {(field) => (
                          <field.DateField
                            label="Report due date"
                            labelClassName={FIELD_LABEL_CLASS}
                          />
                        )}
                      </form.AppField>
                    </div>
                  </ModalSection>

                  {/* MDRS-230: admin-only. The doctor select is new here — without
                      it the toggle could release a case with no doctor assigned.
                      Both write to separate admin endpoints, so they sit outside
                      the form's field set and are applied on submit. */}
                  {isAdmin && (
                    <ModalSection
                      title={DOCTOR_ACCESS_COPY.accessTitle}
                      description={DOCTOR_ACCESS_COPY.accessCardHint}
                      aside={<DoctorAccessBadge state={accessState} />}
                    >
                      <div className="space-y-2">
                        <Label className={FIELD_LABEL_CLASS}>
                          {DOCTOR_ACCESS_COPY.doctorLabel}
                        </Label>
                        <SearchableSelect
                          ariaLabel={DOCTOR_ACCESS_COPY.doctorLabel}
                          value={doctorId}
                          disabled={isUsersLoading}
                          placeholder={DOCTOR_ACCESS_COPY.doctorPlaceholder}
                          searchPlaceholder="Search doctors…"
                          options={doctors}
                          onChange={handleDoctorSelect}
                        />
                      </div>

                      <div className="flex items-center justify-between gap-6 border-t border-foreground-muted/15 pt-4">
                        <div className="min-w-0">
                          <Label
                            htmlFor="is-doctor-ready"
                            className={FIELD_LABEL_CLASS}
                          >
                            {DOCTOR_ACCESS_COPY.readyLabel}
                          </Label>
                          <p className="mt-1 text-xs text-foreground-muted">
                            {readyNoteFor(accessState, selectedDoctorName)}
                          </p>
                        </div>
                        <Switch
                          id="is-doctor-ready"
                          checked={isDoctorReady}
                          // Releasing needs a doctor to release TO; turning it
                          // back off is always allowed so an already-released
                          // case can be corrected.
                          disabled={
                            (!doctorId && !isDoctorReady) || setDoctorReady.isPending
                          }
                          aria-label={DOCTOR_ACCESS_COPY.readyLabel}
                          onCheckedChange={() => setConfirmOpen(true)}
                        />
                      </div>
                    </ModalSection>
                  )}
                </ModalCanvas>

                <DialogFooter>
                  <DialogClose asChild>
                    {/* simple-outline (neutral gray) — Cancel is a dismiss
                        action, not a branded secondary CTA, so it shouldn't
                        share the blue "outline" variant's color. */}
                    <Button
                      type="button"
                      variant="simple-outline"
                      size="sm"
                      disabled={updateCase.isPending || setDoctorReady.isPending}
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </DialogClose>
                  <form.SubmitButton size="sm">
                    {updateCase.isPending || setDoctorReady.isPending
                      ? "Updating..."
                      : "Update case"}
                  </form.SubmitButton>
                </DialogFooter>
              </form>
            </form.AppForm>
          </>
        )}
      </DialogContent>

      {/* MDRS-230: confirm before changing visibility. Applied to local state —
          the change persists on submit with the rest of the form. */}
      <ReleaseConfirmDialog
        open={confirmOpen}
        releasing={!isDoctorReady}
        onConfirm={() => {
          setIsDoctorReady((prev) => !prev);
          setConfirmOpen(false);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </Dialog>
  );
};
